import type { PriceDropFieldItem } from './drops-field-items'
import { DROPS_CITY_SLUGS, cityLabel } from './drops-constants'

export type PriceDropBandKey = 'under-5' | '5-10' | '10-plus'

export type PriceDropBand = {
  key: PriceDropBandKey
  /** Visible label. No inventory lecture — the set size is not the name. */
  label: string
  items: PriceDropFieldItem[]
}

export type PriceDropCityDoor = {
  slug: string
  label: string
  href: string
  /** How many of the listed cuts are in this city: a count of the rows on the page, nothing more. */
  count: number
}

const BANDS: ReadonlyArray<{
  key: PriceDropBandKey
  label: string
  match: (pct: number | null | undefined) => boolean
}> = [
  {
    key: 'under-5',
    label: 'Under 5%',
    match: (pct) => pct != null && Number.isFinite(pct) && pct > 0 && pct < 5,
  },
  {
    key: '5-10',
    label: '5 to 10%',
    match: (pct) => pct != null && Number.isFinite(pct) && pct >= 5 && pct < 10,
  },
  {
    key: '10-plus',
    label: '10% and up',
    match: (pct) => pct != null && Number.isFinite(pct) && pct >= 10,
  },
]

/**
 * Cut-size bands for the opening switch. Each home sits in exactly one band
 * so the dials do not repeat the same address (2026-10-13 review: All cuts
 * plus the three size bands reprinted every house). Empty bands are omitted
 * so a week with no 10% markdowns does not offer a door into nothing.
 */
export function priceDropBands(items: readonly PriceDropFieldItem[]): PriceDropBand[] {
  return BANDS.map((band) => ({
    key: band.key,
    label: band.label,
    items: items.filter((item) => band.match(item.cutPct)),
  })).filter((band) => band.items.length > 0)
}

/**
 * City doors that have at least one photographed cut on this pull, and that
 * this family actually pre-renders. Sorted by the same drop-percent order
 * the Field already uses (first seen = deepest remaining).
 */
export function priceDropCityDoors(items: readonly PriceDropFieldItem[]): PriceDropCityDoor[] {
  const bySlug = new Map<string, PriceDropCityDoor>()
  for (const item of items) {
    const slug = item.citySlug?.trim()
    if (!slug || !DROPS_CITY_SLUGS.includes(slug)) continue
    const door = bySlug.get(slug)
    if (door) {
      door.count += 1
      continue
    }
    bySlug.set(slug, {
      slug,
      label: item.city?.trim() || cityLabel(slug),
      href: `/price-drops/${slug}`,
      count: 1,
    })
  }
  return [...bySlug.values()]
}
